import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from 'react-router-dom';
import { RealtimeChannel } from '@supabase/supabase-js';
import { fetchLiveVisitorsActiveWithin, sendBeaconLiveVisitorHeartbeat, upsertLiveVisitorHeartbeat } from '@/hooks/presence/liveVisitorsDb';

export interface VisitorPresence {
  id: string;
  page: string;
  device: 'desktop' | 'mobile' | 'tablet';
  browser: string;
  os: string;
  timestamp: Date;
  country?: string;
  city?: string;
  isAdmin?: boolean;
}

// Detect device type
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Detect OS
const getOS = (): string => {
  const ua = navigator.userAgent;
  // Important: Android user-agent often contains "Linux", so check Android first
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
};

// Generate unique visitor ID stored in Web Storage (best-effort; some mobile browsers can throw)
const getVisitorId = (): string => {
  const key = 'presence_visitor_id';

  const read = () => {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    }
  };

  const write = (value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  };

  let visitorId = read();
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    write(visitorId);
  }
  return visitorId;
};

const CHANNEL_NAME = 'realtime-presence';

// Hook for tracking the current visitor's presence
export const useTrackPresence = () => {
  const location = useLocation();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const visitorIdRef = useRef<string>(getVisitorId());
  const isSubscribedRef = useRef<boolean>(false);
  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    // Defer initialization to not block critical rendering path
    const initTimeout = window.setTimeout(() => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      
      const visitorId = visitorIdRef.current;
      
      // Reset subscription state on mount
      isSubscribedRef.current = false;

      // Use a stable presence key so each browser tab is tracked separately
      const channel = supabase.channel(CHANNEL_NAME, {
        config: {
          presence: {
            key: visitorId,
          },
        },
      });

      const trackPresence = async () => {
        const presenceData = {
          visitorId: visitorId,
          page: location.pathname,
          device: getDeviceType(),
          browser: getBrowser(),
          os: getOS(),
          timestamp: new Date().toISOString(),
          isAdmin: false,
        };

        // Only track via realtime if subscribed
        if (isSubscribedRef.current) {
          try {
            await channel.track(presenceData);
          } catch {
            // Realtime blocked (e.g. corporate firewall) - DB heartbeat handles this
          }
        }

        // Always write heartbeat to DB (fallback that doesn't rely on websocket)
        try {
          await upsertLiveVisitorHeartbeat({
            visitorId,
            page: presenceData.page,
            device: presenceData.device,
            browser: presenceData.browser,
            os: presenceData.os,
          });
        } catch {
          // Network issues - silently ignore
        }
      };

      // Send initial DB heartbeat (doesn't require realtime subscription)
      upsertLiveVisitorHeartbeat({
        visitorId,
        page: location.pathname,
        device: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
      }).catch(() => {});

      channel
        .on('presence', { event: 'sync' }, () => {
          // Synced - no logging needed in production
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            isSubscribedRef.current = true;
            await trackPresence();
          } else if (status === 'TIMED_OUT' || status === 'CLOSED' || status === 'CHANNEL_ERROR') {
            isSubscribedRef.current = false;
          }
        });

      channelRef.current = channel;
    }, 2000); // Defer by 2 seconds to not block initial render

    return () => {
      window.clearTimeout(initTimeout);
      isSubscribedRef.current = false;
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, []);

  // Update presence when page changes
  useEffect(() => {
    const channel = channelRef.current;
    const visitorId = visitorIdRef.current;

    const presenceData = {
      visitorId: visitorId,
      page: location.pathname,
      device: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      timestamp: new Date().toISOString(),
      isAdmin: false,
    };

    // Try realtime update only if channel exists and is subscribed (non-blocking)
    // Additional safety: check channel state before tracking
    if (channel && isSubscribedRef.current) {
      try {
        channel.track(presenceData).catch(() => {
          // Silently ignore - DB heartbeat handles this
        });
      } catch {
        // Channel not ready - silently ignore
      }
    }

    // Always update DB heartbeat
    upsertLiveVisitorHeartbeat({
      visitorId,
      page: presenceData.page,
      device: presenceData.device,
      browser: presenceData.browser,
      os: presenceData.os,
    }).catch(() => {
      // Silently ignore network issues
    });
  }, [location.pathname]);

  // Keep heartbeat alive even if the user doesn't navigate
  useEffect(() => {
    const visitorId = visitorIdRef.current;
    const interval = window.setInterval(() => {
      upsertLiveVisitorHeartbeat({
        visitorId,
        page: location.pathname,
        device: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
      }).catch(() => {
        // ignore - transient network issues
      });
    }, 25000);

    const sendFinalPing = () => {
      sendBeaconLiveVisitorHeartbeat({
        visitorId,
        page: location.pathname,
        device: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
      });
    };

    // iOS throttles timers in background; use beacon on pagehide/when tab becomes hidden.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendFinalPing();
    };

    window.addEventListener('pagehide', sendFinalPing);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('pagehide', sendFinalPing);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [location.pathname]);
};

// Hook for listening to all visitors (admin use)
export const useLiveVisitors = () => {
  const [realtimeVisitors, setRealtimeVisitors] = useState<VisitorPresence[]>([]);
  const [dbVisitors, setDbVisitors] = useState<VisitorPresence[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const visitors = useMemo(() => {
    const map = new Map<string, VisitorPresence>();

    const upsertNewest = (v: VisitorPresence) => {
      const existing = map.get(v.id);
      if (!existing || v.timestamp.getTime() > existing.timestamp.getTime()) {
        map.set(v.id, v);
      }
    };

    // Merge by latest timestamp (prevents stale realtime from overriding fresher DB heartbeat)
    dbVisitors.forEach(upsertNewest);
    realtimeVisitors.forEach(upsertNewest);

    return Array.from(map.values()).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [dbVisitors, realtimeVisitors]);

  useEffect(() => {
    const adminId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    const channel = supabase.channel(CHANNEL_NAME, {
      config: {
        presence: {
          key: adminId,
        },
      },
    });

    const parsePresenceState = () => {
      const state = channel.presenceState();
      const allVisitors: VisitorPresence[] = [];

      Object.entries(state).forEach(([, presences]) => {
        if (Array.isArray(presences)) {
          presences.forEach((presence: any) => {
            if (presence.isAdmin === true) return;
            if (!presence.visitorId) return;

            allVisitors.push({
              id: presence.visitorId,
              page: presence.page || '/',
              device: presence.device || 'desktop',
              browser: presence.browser || 'Unknown',
              os: presence.os || 'Unknown',
              timestamp: new Date(presence.timestamp || Date.now()),
              country: presence.country,
              city: presence.city,
            });
          });
        }
      });

      // IMPORTANT: don't overwrite the UI with an empty realtime list;
      // mobile often fails realtime, but DB heartbeat still works.
      if (allVisitors.length > 0) {
        setRealtimeVisitors(allVisitors);
      }
    };

    channel
      .on('presence', { event: 'sync' }, parsePresenceState)
      .on('presence', { event: 'join' }, parsePresenceState)
      .on('presence', { event: 'leave' }, parsePresenceState)
      .subscribe(async (status) => {
        // Important: in some networks the realtime websocket is blocked and you'll see TIMED_OUT
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          await channel.track({
            visitorId: adminId,
            page: '/adminmater',
            isAdmin: true,
            timestamp: new Date().toISOString(),
          });
          setTimeout(parsePresenceState, 500);
        }
        if (status === 'TIMED_OUT' || status === 'CLOSED') {
          setIsConnected(false);
        }
      });

    return () => {
      channel.unsubscribe();
      setIsConnected(false);
    };
  }, []);

  // Fallback: poll DB heartbeats (always, since it's the most reliable on mobile)
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        // Use a wider window because mobile browsers can throttle timers in background
        const { data, error } = await fetchLiveVisitorsActiveWithin(600);
        if (error) throw error;

        if (!cancelled) {
          const mapped: VisitorPresence[] = (data ?? []).map((row: any) => ({
            id: row.visitor_id,
            page: row.page || '/',
            device: (row.device || 'desktop') as any,
            browser: row.browser || 'Unknown',
            os: row.os || 'Unknown',
            timestamp: new Date(row.last_seen || row.created_at || Date.now()),
          }));
          setDbVisitors(mapped);
        }
      } catch (e) {
        // ignore - network issues
      }
    };

    tick();
    const interval = window.setInterval(tick, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return { visitors, isConnected, liveCount: visitors.length };
};
